
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Edit, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { getRoleTypeColor, getRoleColor } from "./utils/roleUtils";
import { formatDate } from "./utils/dateUtils";
import { sortAssignmentsByHierarchy } from "./utils/assignmentSortUtils";
import type { ResourceAssignment } from "./types";

interface ResourcesTableProps {
  assignments: ResourceAssignment[];
  onEditResource: (assignment: ResourceAssignment) => void;
  onDeleteResource: (assignmentId: string) => void;
  isRestrictedUser?: boolean;
}

const ResourcesTable = ({ assignments, onEditResource, onDeleteResource, isRestrictedUser = false }: ResourcesTableProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);

  const handleDeleteClick = (assignmentId: string) => {
    setResourceToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (resourceToDelete) {
      onDeleteResource(resourceToDelete);
      setDeleteDialogOpen(false);
      setResourceToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setResourceToDelete(null);
  };

  // Sort assignments by hierarchy before rendering
  const sortedAssignments = sortAssignmentsByHierarchy(assignments);

  return (
    <>
      <ScrollArea className="h-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-primary-light text-[10px] w-[3%]">SN</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[15%]">Name</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[10%]">Role Type</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[12%]">Role</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[18%]">Email</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[8%]">Start Date</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[8%]">End Date</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[7%]">Duration</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[6%]">Rate</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[5%]">Hours</TableHead>
              <TableHead className="text-primary-light text-[10px] w-[8%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAssignments.map((assignment, index) => (
              <TableRow key={assignment.id}>
                <TableCell className="text-white text-[9px] w-[3%]">{index + 1}</TableCell>
                <TableCell className="text-white text-[9px] w-[15%] whitespace-nowrap overflow-hidden text-ellipsis">{assignment.name}</TableCell>
                <TableCell className="text-white text-[9px] w-[10%]">
                  <Badge className={`text-[9px] whitespace-nowrap ${getRoleTypeColor(assignment.role_type)}`}>
                    {assignment.role_type ? assignment.role_type.replace('-', ' ') : 'N/A'}
                  </Badge>
                </TableCell>
                <TableCell className="text-white text-[9px] w-[12%]">
                  <Badge className={`text-[9px] whitespace-nowrap ${getRoleColor(assignment.role)}`}>
                    {assignment.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-white text-[9px] w-[18%] whitespace-nowrap overflow-hidden text-ellipsis">
                  {assignment.email || 'N/A'}
                </TableCell>
                <TableCell className="text-white text-[9px] w-[8%] whitespace-nowrap">{formatDate(assignment.start_date)}</TableCell>
                <TableCell className="text-white text-[9px] w-[8%] whitespace-nowrap">{formatDate(assignment.end_date)}</TableCell>
                <TableCell className="text-white text-[9px] w-[7%] whitespace-nowrap">{assignment.duration} days</TableCell>
                <TableCell className="text-white text-[9px] w-[6%] whitespace-nowrap">${(assignment.rate ?? 0)}/hr</TableCell>
                <TableCell className="text-white text-[9px] w-[5%] whitespace-nowrap">{assignment.allocation_hrs}</TableCell>
                <TableCell className="w-[8%]">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEditResource(assignment)}
                      disabled={isRestrictedUser}
                      className={`h-8 w-8 p-0 ${
                        isRestrictedUser 
                          ? "cursor-not-allowed opacity-50" 
                          : ""
                      }`}
                    >
                      <Edit className={`h-3 w-3 ${
                        isRestrictedUser 
                          ? "text-gray-400" 
                          : "text-primary"
                      }`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(assignment.id)}
                      disabled={isRestrictedUser}
                      className={`h-8 w-8 p-0 ${
                        isRestrictedUser 
                          ? "cursor-not-allowed opacity-50" 
                          : ""
                      }`}
                    >
                      <Trash2 className={`h-3 w-3 ${
                        isRestrictedUser 
                          ? "text-gray-400" 
                          : "text-red-400"
                      }`} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this resource assignment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ResourcesTable;
